/**
 * Animation Type Definitions
 * Timeline-driven animation system for .slate format
 * Uses Web Animations API for playback
 */

// ============================================================================
// EASING
// ============================================================================

/**
 * Standard CSS easing functions
 */
export type EasingFunction =
  | 'linear'
  | 'ease'
  | 'ease-in'
  | 'ease-out'
  | 'ease-in-out'
  // Common curves
  | 'ease-in-quad'
  | 'ease-out-quad'
  | 'ease-in-out-quad'
  | 'ease-in-cubic'
  | 'ease-out-cubic'
  | 'ease-in-out-cubic'
  | 'ease-in-quart'
  | 'ease-out-quart'
  | 'ease-in-out-quart'
  | 'ease-in-expo'
  | 'ease-out-expo'
  | 'ease-in-out-expo'
  | 'ease-in-back'
  | 'ease-out-back'
  | 'ease-in-out-back'
  | 'ease-in-elastic'
  | 'ease-out-elastic'
  | 'ease-in-out-elastic'
  | 'ease-out-bounce';

/**
 * Custom cubic-bezier easing
 */
export interface CubicBezierEasing {
  type: 'cubic-bezier';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Spring-based easing (for physics-like animations)
 */
export interface SpringEasing {
  type: 'spring';
  stiffness: number;  // 100-1000 typical
  damping: number;    // 10-100 typical
  mass: number;       // 0.1-10 typical
}

export type Easing = EasingFunction | CubicBezierEasing | SpringEasing;

// ============================================================================
// ANIMATABLE PROPERTIES
// ============================================================================

/**
 * Properties that can be animated on a layer
 */
export type AnimatableProperty =
  // Position
  | 'x'
  | 'y'
  // Size
  | 'width'
  | 'height'
  | 'scale'
  | 'scaleX'
  | 'scaleY'
  // Transform
  | 'rotation'
  | 'skewX'
  | 'skewY'
  // Visibility
  | 'opacity'
  | 'visible'
  // Color (for shapes/text)
  | 'fill'
  | 'stroke'
  | 'color'
  | 'backgroundColor'
  // Text-specific
  | 'fontSize'
  | 'letterSpacing'
  // Shape-specific
  | 'borderRadius'
  | 'strokeWidth'
  // Content (for text/image changes)
  | 'content';

/**
 * Property value types
 */
export type PropertyValue = number | string | boolean;

// ============================================================================
// KEYFRAMES
// ============================================================================

/**
 * Single keyframe in an animation
 */
export interface Keyframe {
  /**
   * Time offset in milliseconds from track start
   */
  time: number;

  /**
   * Property values at this keyframe
   */
  properties: Partial<Record<AnimatableProperty, PropertyValue>>;

  /**
   * Easing to use when interpolating TO this keyframe
   * If not specified, uses track's default easing
   */
  easing?: Easing;
}

// ============================================================================
// ANIMATION TRACKS
// ============================================================================

/**
 * A track animates a single layer over time
 */
export interface AnimationTrack {
  /**
   * Unique track identifier
   */
  id: string;

  /**
   * Human-readable name
   */
  name: string;

  /**
   * Layer ID this track animates
   */
  layerId: string;

  /**
   * Whether track is enabled
   */
  enabled: boolean;

  /**
   * When the track starts (ms from timeline start)
   */
  startTime: number;

  /**
   * Track duration (ms) - if not set, calculated from keyframes
   */
  duration?: number;

  /**
   * Default easing for keyframes that don't specify one
   */
  easing: Easing;

  /**
   * Keyframes defining the animation
   */
  keyframes: Keyframe[];

  /**
   * How the animation repeats
   */
  repeat?: {
    count: number | 'infinite';
    direction: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
    delay?: number; // Delay between iterations (ms)
  };

  /**
   * What happens before/after the animation
   */
  fillMode?: 'none' | 'forwards' | 'backwards' | 'both';
}

// ============================================================================
// TIMELINE
// ============================================================================

/**
 * Complete timeline containing all animation tracks
 */
export interface Timeline {
  /**
   * Timeline format version
   */
  version: '1.0';

  /**
   * Total timeline duration in milliseconds
   * If not set, calculated from tracks
   */
  duration?: number;

  /**
   * Whether timeline loops
   */
  loop: boolean;

  /**
   * Playback speed multiplier
   */
  playbackRate: number;

  /**
   * Animation tracks
   */
  tracks: AnimationTrack[];
}

// ============================================================================
// PLAYBACK STATE
// ============================================================================

/**
 * Current playback state
 */
export type PlaybackState = 'idle' | 'playing' | 'paused' | 'finished';

/**
 * Playback control interface
 */
export interface PlaybackController {
  state: PlaybackState;
  currentTime: number;
  duration: number;
  playbackRate: number;

  play(): void;
  pause(): void;
  stop(): void;
  seek(time: number): void;
  setPlaybackRate(rate: number): void;
}

// ============================================================================
// PRESETS
// ============================================================================

/**
 * Pre-built animation effects
 */
export type AnimationPreset =
  // Entrances
  | 'fadeIn'
  | 'fadeInUp'
  | 'fadeInDown'
  | 'fadeInLeft'
  | 'fadeInRight'
  | 'scaleIn'
  | 'slideInUp'
  | 'slideInDown'
  | 'slideInLeft'
  | 'slideInRight'
  | 'bounceIn'
  | 'flipInX'
  | 'flipInY'
  | 'zoomIn'
  // Exits
  | 'fadeOut'
  | 'fadeOutUp'
  | 'fadeOutDown'
  | 'fadeOutLeft'
  | 'fadeOutRight'
  | 'scaleOut'
  | 'slideOutUp'
  | 'slideOutDown'
  | 'slideOutLeft'
  | 'slideOutRight'
  | 'bounceOut'
  | 'zoomOut'
  // Attention
  | 'pulse'
  | 'bounce'
  | 'shake'
  | 'wobble'
  | 'flash'
  | 'heartbeat'
  // Continuous
  | 'spin'
  | 'float'
  | 'breathe';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Create an empty timeline
 */
export function createTimeline(options?: Partial<Timeline>): Timeline {
  return {
    version: '1.0',
    loop: false,
    playbackRate: 1,
    tracks: [],
    ...options,
  };
}

/**
 * Create an animation track
 */
export function createTrack(
  layerId: string,
  name: string,
  keyframes: Keyframe[],
  options?: Partial<AnimationTrack>
): AnimationTrack {
  return {
    id: `track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name,
    layerId,
    enabled: true,
    startTime: 0,
    easing: 'ease-out',
    keyframes,
    ...options,
  };
}

/**
 * Create a keyframe
 */
export function createKeyframe(
  time: number,
  properties: Keyframe['properties'],
  easing?: Easing
): Keyframe {
  return {
    time,
    properties,
    ...(easing && { easing }),
  };
}

/**
 * Calculate timeline duration from tracks
 */
export function calculateTimelineDuration(tracks: AnimationTrack[]): number {
  if (tracks.length === 0) return 0;

  return Math.max(
    ...tracks.map(track => {
      const trackEnd = track.startTime + (track.duration ?? getTrackDuration(track));
      return trackEnd;
    })
  );
}

/**
 * Calculate track duration from keyframes
 */
export function getTrackDuration(track: AnimationTrack): number {
  if (track.duration !== undefined) return track.duration;
  if (track.keyframes.length === 0) return 0;

  return Math.max(...track.keyframes.map(kf => kf.time));
}

/**
 * Convert easing to CSS easing string
 */
export function easingToCSS(easing: Easing): string {
  if (typeof easing === 'string') {
    // Standard easings
    const standardEasings: Record<string, string> = {
      'linear': 'linear',
      'ease': 'ease',
      'ease-in': 'ease-in',
      'ease-out': 'ease-out',
      'ease-in-out': 'ease-in-out',
      // Quad
      'ease-in-quad': 'cubic-bezier(0.55, 0.085, 0.68, 0.53)',
      'ease-out-quad': 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      'ease-in-out-quad': 'cubic-bezier(0.455, 0.03, 0.515, 0.955)',
      // Cubic
      'ease-in-cubic': 'cubic-bezier(0.55, 0.055, 0.675, 0.19)',
      'ease-out-cubic': 'cubic-bezier(0.215, 0.61, 0.355, 1)',
      'ease-in-out-cubic': 'cubic-bezier(0.645, 0.045, 0.355, 1)',
      // Quart
      'ease-in-quart': 'cubic-bezier(0.895, 0.03, 0.685, 0.22)',
      'ease-out-quart': 'cubic-bezier(0.165, 0.84, 0.44, 1)',
      'ease-in-out-quart': 'cubic-bezier(0.77, 0, 0.175, 1)',
      // Expo
      'ease-in-expo': 'cubic-bezier(0.95, 0.05, 0.795, 0.035)',
      'ease-out-expo': 'cubic-bezier(0.19, 1, 0.22, 1)',
      'ease-in-out-expo': 'cubic-bezier(1, 0, 0, 1)',
      // Back
      'ease-in-back': 'cubic-bezier(0.6, -0.28, 0.735, 0.045)',
      'ease-out-back': 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      'ease-in-out-back': 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      // Elastic/Bounce - approximations (true elastic needs JS)
      'ease-in-elastic': 'cubic-bezier(0.5, -0.5, 0.75, -0.5)',
      'ease-out-elastic': 'cubic-bezier(0.25, 1.5, 0.5, 1.5)',
      'ease-in-out-elastic': 'cubic-bezier(0.5, -0.5, 0.5, 1.5)',
      'ease-out-bounce': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    };
    return standardEasings[easing] ?? 'ease';
  }

  if (easing.type === 'cubic-bezier') {
    return `cubic-bezier(${easing.x1}, ${easing.y1}, ${easing.x2}, ${easing.y2})`;
  }

  // Spring easing requires JS implementation
  // Return a close approximation
  if (easing.type === 'spring') {
    return 'cubic-bezier(0.175, 0.885, 0.32, 1.275)';
  }

  return 'ease';
}

/**
 * Check if a property value needs special interpolation
 */
export function requiresSpecialInterpolation(property: AnimatableProperty): boolean {
  return ['fill', 'stroke', 'color', 'backgroundColor', 'content'].includes(property);
}
